import { ArrowUp, PanelRight, Square } from "lucide-react";
import type { FormEvent, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

/**
 * The session screen's frame as markup. WorkspaceSessionScreen owns the data,
 * scrolling and panels and renders its chrome through these pieces, so a
 * fixture session composes the same toolbar, transcript column and composer
 * frame.
 */

/** The session page itself, beneath the app menu bar. */
export const SESSION_SCREEN_MAIN_CLASS =
  "relative flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-background";

/** The scroll container around the transcript column. */
export const SESSION_TRANSCRIPT_FRAME_CLASS =
  "mlx-session-transcript-frame h-full min-h-0 overflow-y-auto scrollbar-thin";

/** The transcript's reading column; bottom padding depends on what floats
 *  over it and is added by the caller. */
export const SESSION_TRANSCRIPT_COLUMN_CLASS =
  "mlx-session-chat-shell mx-auto flex w-full max-w-[840px] flex-col gap-9 px-4 py-6 transition-[padding-top] duration-200 [--mlx-session-chat-max-width:840px] motion-reduce:transition-none sm:px-6";

/** The composer's measure, and everything that sits above it. */
export const SESSION_DETAIL_SURFACE_CLASS =
  "mlx-session-chat-shell mx-auto w-[calc(100%-2rem)] max-w-[776px] [--mlx-session-chat-max-width:776px] sm:w-[calc(100%-5.5rem)]";

export function SessionToolbar(props: {
  title: ReactNode;
  /** Plain-text title for the hover tooltip. */
  titleText: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mlx-page-toolbar">
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
        <h1 className="mlx-page-toolbar-title" title={props.titleText}>
          {props.title}
        </h1>
        <div className="mlx-page-toolbar-actions">{props.actions}</div>
      </div>
    </header>
  );
}

export function SessionDetailsButton(props: {
  active: boolean;
  onClick?: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={props.onClick}
      aria-expanded={props.active}
      className={props.active ? "border-primary/20 bg-primary/10 text-primary" : undefined}
    >
      <PanelRight data-icon="inline-start" aria-hidden />
      Details
    </Button>
  );
}

/** The band beneath the transcript that holds the composer. */
export function SessionComposerFrame(props: { children: ReactNode }) {
  return (
    <div className="mlx-session-composer-frame relative shrink-0 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3">
      {props.children}
    </div>
  );
}

/**
 * Above the composer after an answer: the run trace pill on the left and,
 * once the last run has finished, the feedback prompt on the right.
 */
export function SessionAnswerFooter(props: {
  activity?: ReactNode;
  showHelpImprove: boolean;
  onHelpImprove?: () => void;
}) {
  return (
    <div
      className={cn(
        "mb-2 flex flex-wrap items-end justify-between gap-x-3 gap-y-1",
        SESSION_DETAIL_SURFACE_CLASS,
      )}
    >
      {props.activity ?? <span aria-hidden className="min-w-0 flex-1" />}
      {props.showHelpImprove ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-auto h-7 shrink-0 px-2 text-xs font-normal text-muted-foreground hover:text-foreground"
          onClick={props.onHelpImprove}
        >
          Help improve this answer
        </Button>
      ) : null}
    </div>
  );
}

/** The follow-up composer's placeholder, and its wording while a run works. */
export const SESSION_COMPOSER_PLACEHOLDER = "Ask anything about this task…";
export const SESSION_COMPOSER_BUSY_PLACEHOLDER =
  "Type your next question while your Twin finishes…";

/**
 * The follow-up composer as markup: any attached files, the text field, then
 * a footer with attach, voice, model, sources and privacy controls, and send,
 * or stop while a run is working. The controls are slots, so the session
 * screen passes its live ones and a fixture passes the same controls at rest.
 */
export function SessionComposerForm(props: {
  inert?: boolean;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** The text field, usually a PromptChannelMentionInput. */
  textarea: ReactNode;
  /** The files attached to the next message, above the text field (AttachmentCardRow). */
  attachments?: ReactNode;
  error?: string | null;
  attach?: ReactNode;
  dictation?: ReactNode;
  model?: ReactNode;
  /** The sources the Twin may read (ComposerSources). */
  sources?: ReactNode;
  privacy?: ReactNode;
  busy: boolean;
  stopping: boolean;
  onStop?: () => void;
  sendDisabled: boolean;
  sending: boolean;
}) {
  return (
    <form
      inert={props.inert}
      onSubmit={props.onSubmit}
      className={cn(
        "mlx-prompt-composer chat-composer-shadow relative flex flex-col overflow-visible rounded-xl border border-transparent bg-control",
        SESSION_DETAIL_SURFACE_CLASS,
      )}
    >
      {props.attachments}
      {props.textarea}
      <div className="mlx-prompt-footer">
        <div className="flex min-w-0 items-center gap-1">
          {props.error != null ? (
            <div
              className="flex min-w-0 items-center gap-2 truncate text-xs text-destructive"
              aria-live="polite"
            >
              <span className="truncate">{props.error}</span>
            </div>
          ) : null}
          {props.attach}
          {props.dictation}
          {props.model}
          {props.sources}
          {props.privacy}
        </div>
        {props.busy ? (
          <Button
            type="button"
            size="icon"
            variant="destructive"
            disabled={props.stopping}
            className="size-8 shrink-0 rounded-full"
            aria-label={props.stopping ? "Stopping query…" : "Stop query"}
            title="Stop query"
            onClick={props.onStop}
          >
            {props.stopping ? <Spinner aria-hidden /> : <Square aria-hidden />}
          </Button>
        ) : (
          <Button
            type="submit"
            size="icon"
            disabled={props.sendDisabled}
            className="size-8 shrink-0 rounded-full"
            aria-label={props.sending ? "Sending…" : "Send"}
          >
            {props.sending ? <Spinner aria-hidden /> : <ArrowUp aria-hidden />}
          </Button>
        )}
      </div>
    </form>
  );
}
